'use client';

import classNames from 'classnames';
import Link from 'next/link';
import React from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { CaretDownFill } from 'react-bootstrap-icons';

import navStyles from '@/styles/Navigation.module.scss';
import styles from '@/styles/NavMegaMenu.module.scss';

// Generic multi-column hover/click mega-menu for the main nav. Holds all the
// interaction mechanics (hover + click + keyboard, close grace period, custom
// toggle, right-anchored panel, mobile collapse) and is content-agnostic:
// callers compose the panel from the MenuColumn / MenuItem column primitives,
// plus an optional MenuFooter strip via the `footer` prop. See
// DataNavDropdown.tsx and AboutNavDropdown.tsx for worked examples.

// Lets nested links close the menu when followed, without threading a callback
// through every primitive.
const MenuContext = React.createContext<{ close: () => void }>({ close: () => { } });

// Only one mega menu may be open at a time: since all panels open in the same
// place, a panel lingering through its close grace period would overlap the
// incoming one. Opening a menu closes whichever menu registered here.
let closeOpenMenu: (() => void) | null = null;

// Custom toggle so the trigger reads as a nav link (matching the flat NavLinks
// beside it) rather than a Bootstrap button, while still feeding Dropdown the
// onClick/ref it needs for click + keyboard control.
//
// With an `href`, the label navigates and only the caret opens the menu, which
// keeps the menu reachable on touch devices where there is no hover.
type ToggleProps = {
  active: boolean
  label: string
  href?: string
  onClick: (e: React.MouseEvent) => void
}

const MegaToggle = React.forwardRef<HTMLElement, ToggleProps>(
  function MegaToggle({ active, label, href, onClick, ...props }, ref) {
    const className = classNames(
      'nav-link', navStyles.navItem, styles.toggle, { [styles.toggleActive]: active }
    );

    if (href) {
      return (
        <span ref={ref as React.Ref<HTMLSpanElement>} className={className}>
          <Link href={href} className={styles.toggleLink}>{label}</Link>
          <button
            {...props}
            type="button"
            aria-haspopup="true"
            aria-label={`Open the ${label} menu`}
            className={styles.caretButton}
            onClick={onClick}
          >
            <CaretDownFill className={styles.caret} aria-hidden="true" />
          </button>
        </span>
      );
    }

    return (
      <button
        {...props}
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        aria-haspopup="true"
        className={className}
        onClick={onClick}
      >
        {label}
        <CaretDownFill className={styles.caret} aria-hidden="true" />
      </button>
    );
  }
);

type NavMegaMenuProps = {
  label: string
  active: boolean
  children: React.ReactNode
  // Section landing page; see MegaToggle.
  href?: string
  // Optional quiet strip rendered full-width below the columns, for low-traffic
  // links (e.g. legal pages) that shouldn't compete with the main menu items.
  footer?: React.ReactNode
}

export default function NavMegaMenu({ label, active, children, href, footer }: NavMegaMenuProps) {
  const [show, setShow] = React.useState(false);
  // Grace period on mouse-out so moving the pointer between the toggle and the
  // menu doesn't snap it shut — the pointer path can be quite diagonal now that
  // the panel is pinned to the container edge rather than under the toggle.
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeNow = React.useCallback(() => setShow(false), []);

  const open = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (closeOpenMenu && closeOpenMenu !== closeNow) closeOpenMenu();
    closeOpenMenu = closeNow;
    setShow(true);
  };
  const close = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setShow(false), 300);
  };

  React.useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (closeOpenMenu === closeNow) closeOpenMenu = null;
  }, [closeNow]);

  const ctx = React.useMemo(() => ({ close: closeNow }), [closeNow]);

  return (
    <MenuContext.Provider value={ctx}>
      <Dropdown
        show={show}
        onToggle={(next) => (next ? open() : closeNow())}
        onMouseEnter={open}
        onMouseLeave={close}
        className={styles.dropdown}
      >
        <Dropdown.Toggle as={MegaToggle} active={active} label={label} href={href} />
        <Dropdown.Menu align="end" className={styles.menu} renderOnMount>
          <div className={styles.columns}>{children}</div>
          {footer && <div className={styles.footer}>{footer}</div>}
        </Dropdown.Menu>
      </Dropdown>
    </MenuContext.Provider>
  );
}

type MenuColumnProps = {
  heading: string
  children: React.ReactNode
}

export function MenuColumn({ heading, children }: MenuColumnProps) {
  return (
    <div className={styles.column}>
      <div className={styles.colHeading}>{heading}</div>
      {children}
    </div>
  );
}

type MenuItemProps = {
  href: string
  title: string
  desc?: string
}

export function MenuItem({ href, title, desc }: MenuItemProps) {
  const { close } = React.useContext(MenuContext);
  return (
    <Link href={href} prefetch={false} className={styles.item} onClick={close}>
      <span className={styles.itemTitle}>{title}</span>
      {desc && <span className={styles.itemDesc}>{desc}</span>}
    </Link>
  );
}

type MenuLinkProps = {
  href: string
  children: React.ReactNode
}

// The quiet strip passed to NavMegaMenu's `footer` slot: an optional label
// followed by a row of low-traffic MenuFooterLink children (e.g. legal pages).
export function MenuFooter({ label, children }: { label?: string, children: React.ReactNode }) {
  return (
    <>
      {label && <span className={styles.footerLabel}>{label}</span>}
      <span className={styles.footerLinks}>{children}</span>
    </>
  );
}

export function MenuFooterLink({ href, children }: MenuLinkProps) {
  const { close } = React.useContext(MenuContext);
  return (
    <Link href={href} prefetch={false} className={styles.footerLink} onClick={close}>
      {children}
    </Link>
  );
}

// A footer link promoted to a solid primary button, for the one call-to-action
// that should stand out from the quiet link row (e.g. the customer portal).
export function MenuFooterButton({ href, children }: MenuLinkProps) {
  const { close } = React.useContext(MenuContext);
  return (
    <Link href={href} prefetch={false} className={classNames('btn', 'btn-primary', 'btn-sm', styles.footerButton)} onClick={close}>
      {children}
    </Link>
  );
}
